import { useCallback, useEffect, useRef } from 'react';

/**
 * When a nested overflow scroller is at its top/bottom edge (or has no
 * overflow), continue the wheel gesture on the page instead of trapping it.
 *
 * Returns a callback ref to attach to the scroll container.
 */
export default function useChainScroll() {
  const nodeRef = useRef(null);
  const cleanupRef = useRef(null);

  const detach = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    nodeRef.current = null;
  }, []);

  const attach = useCallback((node) => {
    detach();
    if (!node) return;

    nodeRef.current = node;

    const onWheel = (event) => {
      if (event.ctrlKey) return; // leave pinch-zoom alone
      if (Math.abs(event.deltaY) < Math.abs(event.deltaX)) return;

      const { scrollTop, scrollHeight, clientHeight } = node;
      const maxScroll = Math.max(0, scrollHeight - clientHeight);
      const epsilon = 1;
      const atTop = scrollTop <= epsilon;
      const atBottom = scrollTop >= maxScroll - epsilon;
      const scrollingUp = event.deltaY < 0;
      const scrollingDown = event.deltaY > 0;

      const shouldChain =
        maxScroll <= 0 ||
        (scrollingUp && atTop) ||
        (scrollingDown && atBottom);

      if (!shouldChain) return;

      event.preventDefault();
      window.scrollBy({ top: event.deltaY, left: 0, behavior: 'auto' });
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    cleanupRef.current = () => node.removeEventListener('wheel', onWheel);
  }, [detach]);

  useEffect(() => () => detach(), [detach]);

  return attach;
}
